import { useState } from 'react';
import { VcfWorkstream } from '@/hooks/useWorkstreams';

type WorkstreamPhase = 'assess' | 'design' | 'deploy' | 'migrate' | 'operate' | 'optimise';
type WorkstreamPillar = 'infrastructure' | 'application' | 'security';

export function WorkstreamModal({
  workstream,
  onSave,
  onDelete,
  onClose,
}: {
  workstream: VcfWorkstream | null;
  onSave: (data: { code: string; name: string; description?: string; phase?: WorkstreamPhase; pillar?: WorkstreamPillar; category?: string; isActive?: boolean }) => void;
  onDelete?: () => void;
  onClose: () => void;
}) {
  const [code, setCode] = useState(workstream?.code || '');
  const [name, setName] = useState(workstream?.name || '');
  const [description, setDescription] = useState(workstream?.description || '');
  const [phase, setPhase] = useState<WorkstreamPhase>(workstream?.phase || 'assess');
  const [pillar, setPillar] = useState<WorkstreamPillar>(workstream?.pillar || 'infrastructure');
  const [category, setCategory] = useState(workstream?.category || '');
  const [isActive, setIsActive] = useState(workstream?.isActive ?? true);

  return (
    <div className="dialog-overlay">
      <div className="dialog max-w-lg">
        <div className="dialog-header">
          <h2 className="dialog-title">{workstream ? 'Edit Workstream' : 'Add Workstream'}</h2>
        </div>
        <div className="dialog-body space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Code *</label>
              <input type="text" value={code} onChange={(e) => setCode(e.target.value)} className="input mt-1 w-full" placeholder="e.g., FSA-01" required />
            </div>
            <div>
              <label className="label">Phase</label>
              <select value={phase} onChange={(e) => setPhase(e.target.value as WorkstreamPhase)} className="input mt-1 w-full">
                <option value="assess">Assess</option>
                <option value="design">Design</option>
                <option value="deploy">Deploy</option>
                <option value="migrate">Migrate</option>
                <option value="operate">Operate</option>
                <option value="optimise">Optimise</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label">Name *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input mt-1 w-full" placeholder="e.g., VCF Foundation Assessment" required />
          </div>
          <div>
            <label className="label">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="input mt-1 w-full" rows={2} placeholder="Optional description" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Pillar</label>
              <select value={pillar} onChange={(e) => setPillar(e.target.value as WorkstreamPillar)} className="input mt-1 w-full">
                <option value="infrastructure">Infrastructure</option>
                <option value="application">Application</option>
                <option value="security">Security</option>
              </select>
            </div>
            <div>
              <label className="label">Category</label>
              <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} className="input mt-1 w-full" placeholder="e.g., VCF" />
            </div>
          </div>
          {workstream && (
            <div className="flex items-center gap-2">
              <input type="checkbox" id="ws-active" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              <label htmlFor="ws-active" className="text-sm">Active</label>
            </div>
          )}
        </div>
        <div className="dialog-footer">
          {onDelete && (
            <button type="button" onClick={onDelete} className="btn-secondary text-red-600 hover:bg-red-50 mr-auto">
              Delete
            </button>
          )}
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={() => onSave({ code, name, description: description || undefined, phase, pillar, category: category || undefined, isActive })} className="btn-primary" disabled={!code.trim() || !name.trim()}>
            {workstream ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
